'use client';

/**
 * SplitPanel Component
 *
 * A layout component that provides an inline split view with a resizable panel.
 * Unlike the overlay DetailPanel, this component makes the panel part
 * of the page layout so both areas remain interactive.
 *
 * Three-tier responsive behavior:
 * - Mobile (below mobileBreakpoint, default 'sm'): Navigate to full page if onMobileOpen provided, else overlay
 * - Tablet (between mobileBreakpoint and overlayBreakpoint): Show overlay panel
 * - Desktop (above overlayBreakpoint, default 'md'): Inline split view with resizable panel
 */

import type { Breakpoint } from '@mui/material';
import { alpha, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useState } from 'react';
import { useLayoutOffset, useReducedMotion } from '../../hooks';
import { glassEffects } from '../../theme/shadows';
import { durations } from '../../theme/transitions';
import { DetailPanel } from '../DetailPanel';
import { ShimmerLoader } from '../ShimmerLoader/ShimmerLoader';
import { ResizeHandle } from './ResizeHandle';
import { registerOpenSplitPanel } from './split-panel-state';

// ============================================================================
// Types
// ============================================================================

export interface SplitPanelProps {
  /** Main content (table, list, etc.) */
  children: React.ReactNode;

  /** Panel content */
  panel: React.ReactNode;

  /** Whether the panel is open */
  panelOpen: boolean;

  /** Callback when panel should close */
  onPanelClose: () => void;

  /**
   * Header content rendered at full width above the split zone.
   * Unlike children, header content does NOT compress when the panel opens —
   * it always spans the full container width.
   * Use for page titles, statistics cards, filters, alerts, etc.
   */
  header?: React.ReactNode;

  /** Initial/default panel width */
  panelWidth?: number;

  /** Minimum width when resizing */
  minPanelWidth?: number;

  /** Maximum width when resizing */
  maxPanelWidth?: number;

  /** Panel position */
  anchor?: 'left' | 'right';

  /** Storage key for persisting width */
  storageKey?: string;

  /**
   * Breakpoint below which to use overlay instead of inline (tablet/desktop threshold).
   * Accepts MUI breakpoint keys ('sm', 'md', etc.) or a numeric pixel value
   * for callers that need a threshold not on the named scale.
   */
  overlayBreakpoint?: Breakpoint | number;

  /**
   * Breakpoint below which to navigate to full page instead of overlay (mobile/tablet threshold).
   * Only applies when onMobileOpen is provided. Same `Breakpoint | number` type as
   * `overlayBreakpoint`.
   * @default 'sm'
   */
  mobileBreakpoint?: Breakpoint | number;

  /** Enable/disable animations */
  animate?: boolean;

  /** ARIA label for the panel */
  panelAriaLabel?: string;

  /**
   * Height offset to subtract from viewport height (e.g., for header/navbar).
   * Can be a number (px) or CSS string (e.g., '64px', '4rem').
   * @default 0
   */
  headerOffset?: number | string;

  /**
   * Gap between the panel top and the content above (e.g., tabs).
   * Creates visual separation so rounded corners are visible.
   * @default 0
   */
  panelTopGap?: number;

  /**
   * On mobile (below mobileBreakpoint), instead of showing an overlay panel,
   * call this callback. Useful for navigating to a full detail page.
   * When provided and on mobile, the panel will not render.
   * Note: Tablets (between mobileBreakpoint and overlayBreakpoint) will still show overlay.
   */
  onMobileOpen?: () => void;

  /**
   * Render a skeleton in place of `panel` while the entity data is loading.
   *
   * Use this when the URL carries a panel identifier (e.g., `?panelId=...`)
   * but the underlying record hasn't been fetched yet. Without it, the
   * panel area renders empty while the main content is compressed for the
   * panel — which looks broken (narrow centered table on otherwise empty
   * page). With it, the panel shows a placeholder so the layout reads as
   * intentional.
   *
   * Pair with `panelOpen={true}`. Pass a custom skeleton via
   * `panelLoadingFallback` if the default doesn't fit the panel's shape.
   *
   * @default false
   */
  panelLoading?: boolean;

  /**
   * Custom skeleton to render when `panelLoading` is true. Defaults to a
   * generic header bar + content rows + footer placeholder that mirrors the
   * common SplitPanelHeader/Content/Footer layout.
   */
  panelLoadingFallback?: React.ReactNode;

  /**
   * Fires after the user finishes a drag. Lets a parent (e.g. ContextRail)
   * mirror the resized width into a CSS variable so the page-content
   * right-padding tracks the panel.
   */
  onResize?: (width: number) => void;

  /**
   * Pixel inset from the anchored viewport edge. When `anchor === 'right'`,
   * the fixed-positioned panel sits at `right: edgeOffset` instead of
   * `right: 0`. When `anchor === 'left'`, the panel sits at `left: edgeOffset`.
   * The children's compensating margin (mr / ml) is bumped by the same
   * amount so the main content area still tracks the visual edge of the panel.
   * Used by ContextRail to keep a 44px collapsed strip visible to the right
   * of the open panel so users can switch tabs without collapsing the rail
   * (Issue #1472 design pass 4). Renamed from `rightOffset` since it applies
   * to either edge.
   * @default 0
   */
  edgeOffset?: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;

// Main-content padding applied inside the SplitPanel so the side panel sits
// flush to the viewport edge while the list/table side still has breathing
// room. Matches PageTemplate's `list` variant: 10px horizontal, no top (the
// PageContentHeader handles top spacing) and no bottom (content runs flush to
// the bottom of the scroll area).
const MAIN_CONTENT_PADDING = { px: 1.25, pt: 0, pb: 0 } as const;

// Animation variants
const easeOutCubic: [number, number, number, number] = [0.215, 0.61, 0.355, 1];

// ============================================================================
// Sub-components
// ============================================================================

const MotionBox = motion.create(Box);

interface InlinePanelProps {
  children: React.ReactNode;
  width: number;
  onResize: (width: number) => void;
  minWidth: number;
  maxWidth: number;
  anchor: 'left' | 'right';
  animate: boolean;
  ariaLabel?: string;
  /** CSS top offset (e.g. '64px') — used for fixed positioning */
  topOffset?: string;
  /** Pixel offset from the anchored edge (default 0) */
  edgeOffset?: number;
}

const InlinePanel: React.FC<InlinePanelProps> = ({
  children,
  width,
  onResize,
  minWidth,
  maxWidth,
  anchor,
  animate,
  ariaLabel,
  topOffset = '0px',
  edgeOffset = 0,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMaterial = theme.designGuideline === 'material';
  const paperBg = theme.palette.background.paper;
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  // Fixed positioning pins the panel to the viewport edge, immune to any
  // parent Container/padding wrappers.  The main content area uses a
  // compensating margin so it doesn't slip under the panel.
  const panelStyles = {
    width,
    display: 'flex',
    flexDirection: 'column' as const,
    ...(isMaterial
      ? {
          background: paperBg,
        }
      : {
          background: isDark ? 'rgba(28, 28, 30, 0.72)' : 'rgba(255, 255, 255, 0.82)',
          backdropFilter: glass.backdropBlur,
          WebkitBackdropFilter: glass.backdropBlur,
        }),
    borderRadius: 0,
    overflow: 'hidden' as const,
    position: 'fixed' as const,
    top: topOffset,
    bottom: 0,
    ...(anchor === 'right' ? { right: edgeOffset } : { left: edgeOffset }),
    zIndex: theme.zIndex.drawer - 1,
  };

  if (animate) {
    return (
      <MotionBox
        role="complementary"
        aria-label={ariaLabel || 'Detail panel'}
        initial={{ width: 0, opacity: 0 }}
        animate={{ width, opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: durations.normal / 1000, ease: easeOutCubic }}
        sx={panelStyles}
      >
        {anchor === 'right' && (
          <ResizeHandle
            anchor="left"
            onResize={onResize}
            currentWidth={width}
            minWidth={minWidth}
            maxWidth={maxWidth}
          />
        )}
        {children}
        {anchor === 'left' && (
          <ResizeHandle
            anchor="right"
            onResize={onResize}
            currentWidth={width}
            minWidth={minWidth}
            maxWidth={maxWidth}
          />
        )}
      </MotionBox>
    );
  }

  return (
    <Box role="complementary" aria-label={ariaLabel || 'Detail panel'} sx={panelStyles}>
      {anchor === 'right' && (
        <ResizeHandle anchor="left" onResize={onResize} currentWidth={width} minWidth={minWidth} maxWidth={maxWidth} />
      )}
      {children}
      {anchor === 'left' && (
        <ResizeHandle anchor="right" onResize={onResize} currentWidth={width} minWidth={minWidth} maxWidth={maxWidth} />
      )}
    </Box>
  );
};

/**
 * Default loading skeleton rendered in the panel area when `panelLoading` is
 * true and no `panelLoadingFallback` was provided. Mirrors the typical
 * SplitPanelHeader / Content / Footer triptych so the layout reads as
 * intentional while the entity data is in flight.
 */
const DefaultPanelSkeleton: React.FC = () => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Loading"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      {/* Header — title placeholder */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${dividerColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <ShimmerLoader variant="circular" width={36} height={36} />
        <ShimmerLoader variant="text" width="55%" height={18} />
      </Box>
      {/* Body — chip row + a few stacked rows */}
      <Box sx={{ flex: 1, minHeight: 0, px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <ShimmerLoader variant="rounded" width={88} height={22} />
          <ShimmerLoader variant="rounded" width={104} height={22} />
        </Box>
        <ShimmerLoader variant="text" width="80%" height={14} />
        <ShimmerLoader variant="text" width="65%" height={14} />
        <ShimmerLoader variant="text" width="50%" height={14} />
        <ShimmerLoader variant="text" width="45%" height={14} />
      </Box>
      {/* Footer — button placeholders */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${dividerColor}`,
          backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.02 : 0),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <ShimmerLoader variant="rounded" width={32} height={32} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ShimmerLoader variant="rounded" width={32} height={32} />
          <ShimmerLoader variant="rounded" width={96} height={32} />
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const SplitPanel: React.FC<SplitPanelProps> = ({
  children,
  panel,
  panelOpen,
  onPanelClose,
  header,
  panelWidth: initialWidth = DEFAULT_WIDTH,
  minPanelWidth = MIN_WIDTH,
  maxPanelWidth = MAX_WIDTH,
  anchor = 'right',
  storageKey,
  overlayBreakpoint = 'md',
  animate = true,
  panelAriaLabel,
  headerOffset = 0,
  panelTopGap: _panelTopGap = 0,
  onMobileOpen,
  mobileBreakpoint = 'sm',
  panelLoading = false,
  panelLoadingFallback,
  onResize,
  edgeOffset = 0,
}) => {
  // Resolve the actual content rendered in the panel area. When loading,
  // either swap to the caller's bespoke skeleton or fall back to the default
  // header/body/footer placeholder so the panel never renders empty while
  // the main content is compressed for it.
  const resolvedPanel = panelLoading ? (panelLoadingFallback ?? <DefaultPanelSkeleton />) : panel;
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const shouldAnimate = animate && !reducedMotion;

  // Three-tier responsive behavior:
  // - Mobile (below mobileBreakpoint): navigate to full page (if onMobileOpen provided)
  // - Tablet (between mobileBreakpoint and overlayBreakpoint): show overlay panel
  // - Desktop (above overlayBreakpoint): inline split view
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  const isTablet = useMediaQuery(theme.breakpoints.between(mobileBreakpoint, overlayBreakpoint));

  // Get layout offset from context if available, otherwise use prop
  const layoutOffset = useLayoutOffset();
  const effectiveOffset = layoutOffset.hasProvider ? layoutOffset.offset : headerOffset;

  // Calculate height based on viewport and header offset
  const offsetValue = typeof effectiveOffset === 'number' ? `${effectiveOffset}px` : String(effectiveOffset);
  const containerHeight = effectiveOffset ? `calc(100vh - ${offsetValue})` : '100vh';

  // Width state with localStorage persistence
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = localStorage.getItem(`split-view-width-${storageKey}`);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed) && parsed >= minPanelWidth && parsed <= maxPanelWidth) {
          return parsed;
        }
      }
    }
    return initialWidth;
  });

  // Handle resize with persistence
  const handleResize = useCallback(
    (newWidth: number) => {
      const clampedWidth = Math.min(Math.max(newWidth, minPanelWidth), maxPanelWidth);
      setPanelWidth(clampedWidth);
      if (storageKey && typeof window !== 'undefined') {
        localStorage.setItem(`split-view-width-${storageKey}`, String(clampedWidth));
      }
      onResize?.(clampedWidth);
    },
    [minPanelWidth, maxPanelWidth, storageKey, onResize],
  );

  // On mobile with onMobileOpen, navigate instead of showing overlay
  // Use useEffect to trigger navigation when panel opens on mobile
  React.useEffect(() => {
    if (isMobile && panelOpen && onMobileOpen) {
      onMobileOpen();
    }
  }, [isMobile, panelOpen, onMobileOpen]);

  // Publish to the shared SplitPanel-open subject so floating UI (AstorWidget,
  // future overlays) can hide / shift out of the way while the panel takes the
  // right half of the screen. Cleanup runs on close OR unmount so we never
  // leave a stale "open" registration behind.
  React.useEffect(() => {
    if (!panelOpen) return undefined;
    return registerOpenSplitPanel();
  }, [panelOpen]);

  // When inside PortalLayout (has provider), use flex to fill the parent's
  // available space instead of computing our own viewport-based height.
  // This prevents a second scrollbar from appearing when both the parent
  // and SplitPanel independently claim overflow.
  const insidePortalLayout = layoutOffset.hasProvider;
  const sizeProps = insidePortalLayout ? { flex: 1, minHeight: 0 } : { height: containerHeight };

  // Mobile: Navigate to full page (if onMobileOpen provided) or show overlay
  if (isMobile) {
    // If onMobileOpen is provided, don't render panel - parent will navigate
    if (onMobileOpen) {
      return (
        <Box sx={{ position: 'relative', width: '100%', ...sizeProps, ...MAIN_CONTENT_PADDING }}>
          {header}
          {children}
        </Box>
      );
    }

    // No onMobileOpen - fall back to overlay on mobile
    return (
      <Box sx={{ position: 'relative', width: '100%', ...sizeProps, ...MAIN_CONTENT_PADDING }}>
        {header}
        {children}
        <DetailPanel
          open={panelOpen}
          onClose={onPanelClose}
          variant="detail"
          width={panelWidth}
          minWidth={minPanelWidth}
          maxWidth={maxPanelWidth}
          anchor={anchor}
          storageKey={storageKey}
          ariaLabel={panelAriaLabel}
          // Forward edgeOffset so the overlay path leaves room for a sibling
          // rail/strip on the same edge (ContextRail's collapsed strip at the
          // tablet 768-1023px breakpoint). On the drawer path inside
          // DetailPanel this becomes `[anchor]: edgeOffset` instead of `0`.
          edgeOffset={edgeOffset}
          // When edgeOffset > 0 the host (e.g. ContextRail) needs a sibling
          // rail/strip visible alongside the open panel. DetailPanel's default
          // `fullScreenOnMobile` / `modalOnTablet` center the overlay and
          // ignore edgeOffset, which would cover the strip at <md and md-lg
          // breakpoints. Force drawer mode whenever edgeOffset is set so the
          // `[anchor]: edgeOffset` inset is actually applied at every size.
          // Round-2 Copilot finding on PR #1802 (IDs 3251914727 / 3252965013).
          fullScreenOnMobile={edgeOffset > 0 ? false : undefined}
          modalOnTablet={edgeOffset > 0 ? false : undefined}
        >
          {resolvedPanel}
        </DetailPanel>
      </Box>
    );
  }

  // Tablet: Always show overlay panel (good balance of space and usability)
  if (isTablet) {
    return (
      <Box sx={{ position: 'relative', width: '100%', ...sizeProps, ...MAIN_CONTENT_PADDING }}>
        {header}
        {children}
        <DetailPanel
          open={panelOpen}
          onClose={onPanelClose}
          variant="detail"
          width={panelWidth}
          minWidth={minPanelWidth}
          maxWidth={maxPanelWidth}
          anchor={anchor}
          storageKey={storageKey}
          ariaLabel={panelAriaLabel}
          // Forward edgeOffset so the overlay path leaves room for a sibling
          // rail/strip on the same edge (ContextRail's collapsed strip at the
          // tablet 768-1023px breakpoint). On the drawer path inside
          // DetailPanel this becomes `[anchor]: edgeOffset` instead of `0`.
          edgeOffset={edgeOffset}
          // When edgeOffset > 0 the host (e.g. ContextRail) needs a sibling
          // rail/strip visible alongside the open panel. DetailPanel's default
          // `fullScreenOnMobile` / `modalOnTablet` center the overlay and
          // ignore edgeOffset, which would cover the strip at <md and md-lg
          // breakpoints. Force drawer mode whenever edgeOffset is set so the
          // `[anchor]: edgeOffset` inset is actually applied at every size.
          // Round-2 Copilot finding on PR #1802 (IDs 3251914727 / 3252965013).
          fullScreenOnMobile={edgeOffset > 0 ? false : undefined}
          modalOnTablet={edgeOffset > 0 ? false : undefined}
        >
          {resolvedPanel}
        </DetailPanel>
      </Box>
    );
  }

  // Desktop: Inline split view — panel uses fixed positioning to pin to the
  // viewport edge, immune to any parent Container/padding wrappers.  The main
  // content area uses a compensating margin so it doesn't slip under the panel.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...sizeProps,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Main content area — margin compensates for the fixed panel */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'auto',
          transition: shouldAnimate ? `margin ${durations.normal}ms ease-out` : undefined,
          // When `edgeOffset` is set, the panel sits inset from the right
          // edge, so the children-side compensating margin must include the
          // offset (panel + strip width) — otherwise the main content slips
          // under the collapsed strip that lives beyond the panel's right edge.
          ...(panelOpen && anchor === 'right' ? { mr: `${panelWidth + edgeOffset}px` } : {}),
          ...(panelOpen && anchor === 'left' ? { ml: `${panelWidth + edgeOffset}px` } : {}),
          ...MAIN_CONTENT_PADDING,
        }}
      >
        {header}
        {children}
      </Box>

      {/* Fixed panel — pinned to viewport edge, above parent padding */}
      <AnimatePresence>
        {panelOpen && (
          <InlinePanel
            width={panelWidth}
            onResize={handleResize}
            minWidth={minPanelWidth}
            maxWidth={maxPanelWidth}
            anchor={anchor}
            animate={shouldAnimate}
            ariaLabel={panelAriaLabel}
            topOffset={offsetValue}
            edgeOffset={edgeOffset}
          >
            {resolvedPanel}
          </InlinePanel>
        )}
      </AnimatePresence>
    </Box>
  );
};

// Stable displayName so PageTemplate can detect SplitPanel children without
// relying on component identity (which breaks under module duplication).
SplitPanel.displayName = 'SplitPanel';

export default SplitPanel;
